import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "smokeyaf_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return parsed.map((p) => ({ ...p, lineKey: p.lineKey || p.id }));
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore
    }
  }, [items]);

  const add = (menuItem) =>
    setItems((prev) => {
      const addons = menuItem.selectedAddons || [];
      const addonIds = addons.map((a) => a.id).sort().join(",");
      let lineKey = menuItem.id;
      if (menuItem.dip) lineKey += `-dip-${menuItem.dip}`;
      if (addonIds) lineKey += `-add-${addonIds}`;
      const unitPrice =
        menuItem.price + addons.reduce((s, a) => s + a.price, 0);
      const found = prev.find((p) => p.lineKey === lineKey);
      if (found) {
        return prev.map((p) =>
          p.lineKey === lineKey ? { ...p, quantity: p.quantity + 1 } : p
        );
      }
      return [
        ...prev,
        {
          lineKey,
          id: menuItem.id,
          name: menuItem.name,
          price: unitPrice,
          image: menuItem.image,
          quantity: 1,
          dip: menuItem.dip || null,
          dipName: menuItem.dipName || null,
          addons,
        },
      ];
    });

  const remove = (lineKey) =>
    setItems((prev) => prev.filter((p) => p.lineKey !== lineKey));

  const setQty = (lineKey, quantity) =>
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((p) => p.lineKey !== lineKey)
        : prev.map((p) => (p.lineKey === lineKey ? { ...p, quantity } : p))
    );

  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const count = items.reduce((s, p) => s + p.quantity, 0);
    const subtotal = items.reduce((s, p) => s + p.quantity * p.price, 0);
    return { count, subtotal };
  }, [items]);

  const value = {
    items,
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    add,
    remove,
    setQty,
    clear,
    ...totals,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
